import { useState, useCallback } from "react";
import { completeCallToAction } from "@/services/api/agentic";
import type { 
  CallToActionCompletionRequest, 
  CallToActionCompletionResponse 
} from "@/services/api/agentic/types";

interface UseCallToActionReturn {
  completeAction: (ctaId: string, request: CallToActionCompletionRequest) => Promise<CallToActionCompletionResponse>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Custom hook for completing call-to-actions
 * 
 * @example
 * ```tsx
 * const { completeAction, isLoading, error } = useCallToAction();
 * 
 * const handleTextInput = async (ctaId: string, value: string) => {
 *   const response = await completeAction(ctaId, {
 *     remedy_type: "text_input",
 *     value
 *   });
 *   
 *   if (response.success) {
 *     console.log("Action completed successfully");
 *   }
 * };
 * 
 * const handleButtonAction = async (ctaId: string, action: string) => {
 *   const response = await completeAction(ctaId, {
 *     remedy_type: "button_action",
 *     button_action: action,
 *     comment: "User approved the action"
 *   });
 * };
 * 
 * const handleDocumentUpload = async (ctaId: string, file: File) => {
 *   const response = await completeAction(ctaId, {
 *     remedy_type: "document_upload",
 *     file,
 *     description: "Latest pension statement"
 *   });
 * };
 * ```
 */
export function useCallToAction(): UseCallToActionReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const completeAction = useCallback(async (
    ctaId: string, 
    request: CallToActionCompletionRequest
  ): Promise<CallToActionCompletionResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await completeCallToAction(ctaId, request);
      
      // If the response has a success field, check it
      if (response && typeof response.success === 'boolean' && !response.success) {
        throw new Error(response.message || "Failed to complete call-to-action");
      }

      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    completeAction,
    isLoading,
    error,
    clearError,
  };
} 